import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PWAInstallModalProps {
  show: boolean;
  onInstall: () => void;
  onClose: () => void;
}

export const PWAInstallModal: React.FC<PWAInstallModalProps> = ({ show, onInstall, onClose }) => {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-6">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#1a2b3c]/60 backdrop-blur-md"
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white w-full max-w-sm rounded-[3rem] p-8 shadow-2xl relative overflow-hidden text-center z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 pointer-events-none opacity-5">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500 rounded-full blur-3xl animate-pulse" />
            </div>

            <div className="relative z-10 flex flex-col items-center gap-6">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center text-blue-500 shadow-inner">
                <Smartphone size={40} className="animate-bounce" style={{ animationDuration: '3s' }} />
              </div>

              <div className="space-y-2">
                <h3 className="text-2xl font-black text-[#1a2b3c] tracking-tight leading-tight">{t('pwa.install_title')}</h3>
                <p className="text-sm font-medium text-gray-500 leading-relaxed px-4">
                  {t('pwa.install_desc')}
                </p>
              </div>

              <div className="flex flex-col w-full gap-3 mt-2">
                <button 
                  onClick={onInstall}
                  className="w-full bg-[#1a2b3c] text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-[#1a2b3c]/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 group"
                >
                  <Download size={16} />
                  {t('pwa.install_btn')}
                </button>
                <button 
                  onClick={onClose}
                  className="w-full py-4 text-gray-400 font-black text-xs uppercase tracking-widest hover:text-[#1a2b3c] transition-colors"
                >
                  {t('pwa.maybe_later')}
                </button>
              </div>
            </div>

            <button 
              onClick={onClose}
              className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-gray-300 hover:text-gray-500 transition-all z-50 rounded-full hover:bg-gray-50"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
